import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CSSProperties, FocusEvent, TransitionEvent } from 'react';

import { css } from '@emotion/react';

import { createPortal } from 'react-dom';

import {
  createZIndex,
  useLocalizedStringFormatter,
} from '@coursera/cds-common';

import i18nMessages from '@core/notifications/i18n';
import {
  transitionDuration,
  transitionEasing,
} from '@core/transitions/constants';
import { useReducedMotion } from '@core/transitions/hooks/useReducedMotion';
import VisuallyHidden from '@core/VisuallyHidden';

import Toast from './Toast';
import type { ToastDismissActivation } from './Toast';
import type { ToastQueueItem, ToastViewportPosition } from './types';
import { useToastAutoDismiss } from './useToastAutoDismiss';
import { useViewportPointerExpanded } from './useViewportPointerExpanded';

/**
 * ## Toast stack interaction
 *
 * **Viewport expansion** uses **`stackExpanded = hovering || focused || exitingToast`**
 * for the region (`data-expanded`), auto-dismiss pause, shell tab-stop eligibility, and
 * measured `minHeight` monotonicity while any toast exits.
 *
 * **Per-row layout** uses **`layoutExpanded = hovering || focused || thisRowExiting`** only:
 * non-exiting rows stay in the collapsed peek/scale stack during a sibling’s exit so they do
 * not briefly animate to the full expanded stack and back (timeout dismiss UX).
 *
 * - **Hovering:** {@link useViewportPointerExpanded} — `pointer-events: none` viewport + hit
 *   pads on items; multi-toast stacks add a **`data-cds-toast-stack-hit-bridge`** layer under
 *   rows so expanded inter-toast gaps still receive pointer events. The viewport also gets a
 *   **measured `minHeight`** from the union of
 *   `[data-toast-id]` row rects so native `mouseenter`/`mouseleave` match the visual stack
 *   (Base UI–style viewport hover). While any toast is exiting, **`minHeight` is monotonic**
 *   (does not shrink) and pointer-leave does not clear `hovering`, avoiding a mid-exit hover
 *   gap. `elementsFromPoint` after `requestAnimationFrame` on leave still avoids spurious
 *   collapse when layout/dismiss fires `mouseleave` while the cursor stays over the stack.
 * - **Focused:** `:focus-visible` on `document.activeElement` when inside the viewport; Tab-key
 *   hint ref covers JSDOM / engines where `:focus-visible` is false after `userEvent.tab()`.
 * - **Auto-dismiss:** paused while `stackExpanded` (hover, keyboard focus, or exit animation).
 * - **SR:** live region + roles live in {@link Toast}; optional
 *   {@link ToastProviderProps.toastShellTabStop} adds focusable shells per visible toast and,
 *   when two or more toasts are shown, a stack-level `role="group"` on the inner stack wrapper.
 *   The wrapper is always present so rows are not reparented when the count drops to one (avoids
 *   replaying the mount entry animation).
 * - **F6:** when at least one toast is visible, moves focus to the viewport `role="region"`
 *   (same landmark shortcut pattern as Base UI Toast docs).
 * - **Escape:** dismisses the newest toast only when `document.activeElement` is **inside**
 *   the viewport (after F6 or Tab into a shell / control), so Escape elsewhere (e.g. closing a
 *   menu) does not clear notifications. Still skipped when focus is inside an `aria-modal` dialog.
 */

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const STACK_GAP = 8;
const STACK_SCALE_STEP = 0.05;
const STACK_PEEK = 8;
const MAX_VISIBLE_BEHIND = 2;
/** Max exit slide (px) as multiple of toast height — avoids huge px↔% interpolations on long expanded stacks. */
const EXIT_TRANSLATE_MAX_HEIGHT_MULTIPLIER = 2;

type ToastDismissSource = ToastDismissActivation | 'programmatic';

function schedulePostDismissKeyboardFocus(root: HTMLElement | null) {
  if (!root) {
    return;
  }
  /* Defer past React commit (microtask + single rAF can run before DOM updates in JSDOM). */
  setTimeout(() => {
    requestAnimationFrame(() => {
      const nextShell = root.querySelector(
        '[data-cds-toast-shell="true"]'
      ) as HTMLElement | null;
      if (nextShell) {
        nextShell.focus({ preventScroll: true });
        return;
      }
      const nextClose = root.querySelector(
        '[data-cds-toast-dismiss="true"]'
      ) as HTMLElement | null;
      if (nextClose) {
        nextClose.focus({ preventScroll: true });
        return;
      }
      root.focus({ preventScroll: true });
    });
  }, 0);
}

/** Fallback if `transitionend` does not fire (e.g. JSDOM). */
const TOAST_EXIT_FALLBACK_MS = transitionDuration.fast + 80;

/* ------------------------------------------------------------------ */
/*  Viewport CSS                                                       */
/*  Default layout height comes from content; we also set `minHeight`   */
/*  from measured toast rects so `mouseenter`/`mouseleave` on the       */
/*  region match the visual stack (see computeStackHitMinHeight).       */
/* ------------------------------------------------------------------ */

/** Vertical span of toast row wrappers vs the viewport so the region box includes overflow. */
function computeStackHitMinHeight(
  root: HTMLElement,
  isBottomStack: boolean
): number {
  const rows = root.querySelectorAll<HTMLElement>('[data-toast-id]');
  if (rows.length === 0) {
    return 0;
  }
  const vr = root.getBoundingClientRect();
  if (isBottomStack) {
    let minTop = Infinity;
    for (let i = 0; i < rows.length; i++) {
      minTop = Math.min(minTop, rows[i].getBoundingClientRect().top);
    }
    if (!Number.isFinite(minTop)) {
      return 0;
    }
    return Math.max(0, Math.ceil(vr.bottom - minTop));
  }
  let maxBottom = -Infinity;
  for (let i = 0; i < rows.length; i++) {
    maxBottom = Math.max(maxBottom, rows[i].getBoundingClientRect().bottom);
  }
  if (!Number.isFinite(maxBottom)) {
    return 0;
  }
  return Math.max(0, Math.ceil(maxBottom - vr.top));
}

const viewportCss = css`
  position: fixed;
  z-index: ${createZIndex.snackbar};
  display: flex;
  flex-direction: column;
  width: min(
    calc(100% - var(--cds-spacing-200) - var(--cds-spacing-200)),
    28rem
  );
  max-width: min(
    calc(100% - var(--cds-spacing-200) - var(--cds-spacing-200)),
    28rem
  );
  pointer-events: none;
`;

/** Fills the viewport so stacked toasts stay positioned; `pointer-events: none` matches viewport. */
const toastStackGroupCss = css`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

/** Hit target for inter-toast gaps in expanded stacks (group is `pointer-events: none`). */
const toastStackHitBridgeCss = css`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: auto;
`;

const positionModifiers: Record<
  ToastViewportPosition,
  ReturnType<typeof css>
> = {
  'bottom-right': css`
    right: var(--cds-spacing-200);
    bottom: var(--cds-spacing-200);
    align-items: flex-end;
  `,
  'bottom-left': css`
    left: var(--cds-spacing-200);
    bottom: var(--cds-spacing-200);
    align-items: flex-start;
  `,
  'bottom-center': css`
    left: 50%;
    bottom: var(--cds-spacing-200);
    transform: translateX(-50%);
    align-items: stretch;
  `,
  'top-right': css`
    right: var(--cds-spacing-200);
    top: var(--cds-spacing-200);
    align-items: flex-end;
  `,
  'top-left': css`
    left: var(--cds-spacing-200);
    top: var(--cds-spacing-200);
    align-items: flex-start;
  `,
  'top-center': css`
    left: 50%;
    top: var(--cds-spacing-200);
    transform: translateX(-50%);
    align-items: stretch;
  `,
};

const toastItemCss = css`
  pointer-events: auto;
  position: absolute;
  left: 0;
  right: 0;
  width: 100%;
  transition: transform ${transitionDuration.normal}ms
      ${transitionEasing.entrance},
    opacity ${transitionDuration.normal}ms ${transitionEasing.entrance};

  &[data-exiting='true'] {
    transition: transform ${transitionDuration.fast}ms ${transitionEasing.exit},
      opacity ${transitionDuration.fast}ms ${transitionEasing.exit};
    pointer-events: none;
  }

  /* Invisible hit-area pads above/below each toast so the pointer
     doesn't momentarily leave the stack when crossing the gap between
     toasts. Without this, mouseLeave fires and restarts auto-dismiss. */
  &::before,
  &::after {
    content: '';
    position: absolute;
    left: -8px;
    right: -8px;
    height: ${STACK_GAP + 12}px;
    pointer-events: auto;
  }

  &::before {
    bottom: 100%;
  }

  &::after {
    top: 100%;
  }
`;

/* ------------------------------------------------------------------ */
/*  Height measurement via ResizeObserver                               */
/* ------------------------------------------------------------------ */

type ROEntry = {
  target: Element;
  borderBoxSize?: Array<{ blockSize: number }>;
  contentBoxSize?: Array<{ blockSize: number }>;
};
type ROCallback = (entries: ROEntry[]) => void;
type ROInstance = {
  observe(el: Element): void;
  unobserve(el: Element): void;
  disconnect(): void;
};

function createResizeObserver(cb: ROCallback): ROInstance | null {
  const g = globalThis as typeof globalThis & {
    ResizeObserver?: new (cb: ROCallback) => ROInstance;
  };
  if (!g.ResizeObserver) {
    return null;
  }
  return new g.ResizeObserver(cb);
}

function useHeightMap() {
  const [heights, setHeights] = useState<Record<string, number>>({});
  const observerRef = useRef<ROInstance | null>(null);
  const nodesRef = useRef<Map<string, HTMLDivElement>>(new Map());

  if (!observerRef.current) {
    observerRef.current = createResizeObserver((entries) => {
      const updates: Record<string, number> = {};
      for (const entry of entries) {
        const el = entry.target as HTMLDivElement;
        const id = el.dataset.toastId;
        if (id) {
          updates[id] =
            entry.borderBoxSize?.[0]?.blockSize ??
            entry.contentBoxSize?.[0]?.blockSize ??
            el.offsetHeight;
        }
      }
      if (Object.keys(updates).length > 0) {
        setHeights((prev) => {
          let changed = false;
          for (const [k, v] of Object.entries(updates)) {
            if (prev[k] !== v) {
              changed = true;
              break;
            }
          }
          return changed ? { ...prev, ...updates } : prev;
        });
      }
    });
  }

  const refCallback = useCallback(
    (id: string) => (node: HTMLDivElement | null) => {
      const prev = nodesRef.current.get(id);
      if (prev && prev !== node) {
        observerRef.current?.unobserve(prev);
        nodesRef.current.delete(id);
      }
      if (node) {
        node.dataset.toastId = id;
        nodesRef.current.set(id, node);
        observerRef.current?.observe(node);
      }
    },
    []
  );

  useEffect(() => {
    const obs = observerRef.current;
    return () => {
      obs?.disconnect();
    };
  }, []);

  return { heights, refCallback };
}

/* ------------------------------------------------------------------ */
/*  ToastViewport                                                      */
/* ------------------------------------------------------------------ */

export type ToastViewportProps = {
  toasts: ToastQueueItem[];
  position: ToastViewportPosition;
  onDismiss: (id: string) => void;
  /** @see ToastProviderProps.renderInPlace */
  renderInPlace?: boolean;
  /** @see ToastProviderProps.toastShellTabStop */
  toastShellTabStop?: boolean;
};

export function ToastViewport({
  toasts,
  position,
  onDismiss,
  renderInPlace = false,
  toastShellTabStop = true,
}: ToastViewportProps) {
  const stringFormatter = useLocalizedStringFormatter(i18nMessages);
  const regionLabel = stringFormatter.format('toast_notifications_region');

  const [hovering, setHovering] = useState(false);
  const [focused, setFocused] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const tabKeyboardFocusMoveRef = useRef(false);
  const pendingKeyboardFocusDismissIdsRef = useRef<Set<string>>(new Set());

  const [exitingIds, setExitingIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const exitingIdsRef = useRef(exitingIds);
  exitingIdsRef.current = exitingIds;

  const deferPointerLeaveDuringExitRef = useRef(false);

  const syncDeferPointerLeaveForExit = useCallback(() => {
    deferPointerLeaveDuringExitRef.current = exitingIdsRef.current.size > 0;
  }, []);

  syncDeferPointerLeaveForExit();

  const userExpanded = hovering || focused;
  const stackExpanded = userExpanded || exitingIds.size > 0;

  const {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onMouseMove: handleMouseMove,
    resetPointerTracking,
    flushPendingPointerLeaveCollapse,
    revalidateHoverIfPointerInsideViewport,
  } = useViewportPointerExpanded({
    viewportRef,
    setHovering,
    pointerTrackingActive: toasts.length > 0,
    deferPointerLeaveCollapseWhileRef: deferPointerLeaveDuringExitRef,
    pinHoverDuringToastExitRef: exitingIdsRef,
  });

  const reducedMotion = useReducedMotion();

  const finishDismiss = useCallback(
    (id: string) => {
      const shouldRestoreKeyboardFocus = pendingKeyboardFocusDismissIdsRef.current.has(
        id
      );
      if (shouldRestoreKeyboardFocus) {
        pendingKeyboardFocusDismissIdsRef.current.delete(id);
      }
      const wasExiting = exitingIdsRef.current.has(id);
      onDismiss(id);
      if (wasExiting) {
        const nextIds = new Set(exitingIdsRef.current);
        nextIds.delete(id);
        exitingIdsRef.current = nextIds;
      }
      setExitingIds((prev) => {
        if (!prev.has(id)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      syncDeferPointerLeaveForExit();
      if (shouldRestoreKeyboardFocus) {
        schedulePostDismissKeyboardFocus(viewportRef.current);
      }
    },
    [onDismiss, syncDeferPointerLeaveForExit]
  );

  const beginDismiss = useCallback(
    (id: string, source: ToastDismissSource = 'programmatic') => {
      if (reducedMotion) {
        onDismiss(id);
        if (source === 'keyboard') {
          schedulePostDismissKeyboardFocus(viewportRef.current);
        }
        return;
      }
      if (source === 'keyboard') {
        pendingKeyboardFocusDismissIdsRef.current.add(id);
      }
      if (!exitingIdsRef.current.has(id)) {
        const nextIds = new Set(exitingIdsRef.current);
        nextIds.add(id);
        exitingIdsRef.current = nextIds;
      }
      setExitingIds((prev) => {
        if (prev.has(id)) {
          return prev;
        }
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      syncDeferPointerLeaveForExit();
    },
    [onDismiss, reducedMotion, syncDeferPointerLeaveForExit]
  );

  const handleFocusCapture = useCallback(() => {
    const root = viewportRef.current;
    if (!root) {
      setFocused(false);
      return;
    }
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !root.contains(active)) {
      setFocused(false);
      return;
    }
    let focusVisibleMatch = false;
    try {
      focusVisibleMatch = active.matches(':focus-visible');
    } catch {
      focusVisibleMatch = false;
    }
    const tabHint = tabKeyboardFocusMoveRef.current;
    tabKeyboardFocusMoveRef.current = false;
    setFocused(focusVisibleMatch || tabHint);
  }, []);

  const handleBlurCapture = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const { currentTarget, relatedTarget } = event;
    if (
      relatedTarget instanceof Node &&
      currentTarget.contains(relatedTarget)
    ) {
      return;
    }
    if (exitingIdsRef.current.size > 0) {
      return;
    }
    setFocused(false);
  }, []);

  useEffect(() => {
    if (exitingIds.size !== 0) {
      return;
    }
    flushPendingPointerLeaveCollapse();
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        revalidateHoverIfPointerInsideViewport();
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [
    exitingIds.size,
    flushPendingPointerLeaveCollapse,
    revalidateHoverIfPointerInsideViewport,
  ]);

  useEffect(() => {
    if (toasts.length === 0) {
      pendingKeyboardFocusDismissIdsRef.current.clear();
      setExitingIds(new Set());
      exitingIdsRef.current = new Set();
      deferPointerLeaveDuringExitRef.current = false;
      resetPointerTracking();
      setHovering(false);
      setFocused(false);
    }
  }, [resetPointerTracking, toasts.length]);

  useEffect(() => {
    if (toasts.length === 0) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        tabKeyboardFocusMoveRef.current = true;
      }
      if (event.key === 'F6') {
        if (toasts.length === 0) {
          return;
        }
        const viewport = viewportRef.current;
        if (!viewport) {
          return;
        }
        event.preventDefault();
        viewport.focus({ preventScroll: false });
        return;
      }
      if (event.key !== 'Escape') {
        return;
      }
      if (event.defaultPrevented) return;
      const viewport = viewportRef.current;
      if (!viewport) {
        return;
      }
      const active = document.activeElement;
      if (!(active instanceof Node) || !viewport.contains(active)) {
        return;
      }
      if (
        active instanceof Element &&
        active.closest('[role="dialog"][aria-modal="true"]')
      ) {
        return;
      }
      event.preventDefault();
      beginDismiss(toasts[toasts.length - 1].id, 'programmatic');
    };
    const onPointerDown = () => {
      tabKeyboardFocusMoveRef.current = false;
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [toasts, beginDismiss]);

  const { heights, refCallback } = useHeightMap();
  const isBottom = position.startsWith('bottom');

  const displayOrder = useMemo(() => [...toasts].reverse(), [toasts]);
  const stackCount = displayOrder.length;
  const useStackGroup = toastShellTabStop && stackCount >= 2;
  const stackGroupHintId = useId();
  const stackGroupHintText = useStackGroup
    ? stringFormatter.format('toast_stack_additional', {
        count: stackCount - 1,
      })
    : undefined;

  const frontHeight =
    displayOrder.length > 0 ? heights[displayOrder[0].id] ?? 0 : 0;

  const offsets = useMemo(() => {
    const result: Record<string, number> = {};
    let cumulative = 0;
    for (let i = 0; i < displayOrder.length; i++) {
      result[displayOrder[i].id] = cumulative;
      const h = heights[displayOrder[i].id] ?? 0;
      cumulative += h + STACK_GAP;
    }
    return result;
  }, [displayOrder, heights]);

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );

  const [stackHitMinHeight, setStackHitMinHeight] = useState(0);
  const exitingIdsKey = useMemo(() => [...exitingIds].sort().join(','), [
    exitingIds,
  ]);
  const heightsKey = useMemo(
    () =>
      Object.keys(heights)
        .sort()
        .map((id) => `${id}:${heights[id] ?? 0}`)
        .join('|'),
    [heights]
  );

  useLayoutEffect(() => {
    const root = viewportRef.current;
    if (!root || toasts.length === 0) {
      setStackHitMinHeight(0);
      return;
    }
    const run = () => {
      const el = viewportRef.current;
      if (!el || toasts.length === 0) {
        return;
      }
      const next = computeStackHitMinHeight(el, isBottom);
      setStackHitMinHeight((prev) => {
        if (exitingIdsRef.current.size > 0) {
          const merged = prev > 0 ? Math.max(prev, next) : next;
          return merged === prev ? prev : merged;
        }
        return next === prev ? prev : next;
      });
    };
    run();
    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(run);
    });
    const t = window.setTimeout(run, transitionDuration.normal + 20);
    return () => {
      cancelAnimationFrame(outerRaf);
      cancelAnimationFrame(innerRaf);
      window.clearTimeout(t);
    };
  }, [
    displayOrder,
    exitingIdsKey,
    heightsKey,
    isBottom,
    position,
    stackExpanded,
    toasts.length,
  ]);

  useLayoutEffect(() => {
    if (renderInPlace) {
      setPortalContainer(null);
      return;
    }
    if (typeof document === 'undefined') {
      return;
    }
    setPortalContainer(document.body);
  }, [renderInPlace]);

  const toastItems = displayOrder.map((item, index) => {
    const isExiting = exitingIds.has(item.id);
    const layoutExpanded = userExpanded || isExiting;
    const shellTabStopEligible =
      toastShellTabStop &&
      !isExiting &&
      (stackExpanded || index === 0 || index <= MAX_VISIBLE_BEHIND);
    return (
      <ToastViewportItem
        key={item.id}
        exitFallbackMs={TOAST_EXIT_FALLBACK_MS}
        expanded={layoutExpanded}
        externalPaused={stackExpanded}
        finishDismiss={finishDismiss}
        frontHeight={frontHeight}
        index={index}
        isBottom={isBottom}
        isExiting={isExiting}
        item={item}
        measureRef={refCallback(item.id)}
        naturalHeight={heights[item.id] ?? 0}
        offsetY={offsets[item.id] ?? 0}
        shellApplyGroupRole={!useStackGroup}
        shellStackedCount={useStackGroup ? 1 : stackCount}
        shellTabStop={shellTabStopEligible}
        stackCount={stackCount}
        onDismissItem={beginDismiss}
      />
    );
  });

  const viewport = (
    <div
      ref={viewportRef}
      aria-label={regionLabel}
      className="cds-Toast-viewport"
      css={[viewportCss, positionModifiers[position]]}
      data-expanded={stackExpanded || undefined}
      role="region"
      style={
        stackHitMinHeight > 0 ? { minHeight: stackHitMinHeight } : undefined
      }
      tabIndex={-1}
      onBlurCapture={handleBlurCapture}
      onFocusCapture={handleFocusCapture}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      <div
        aria-describedby={
          useStackGroup && stackGroupHintText ? stackGroupHintId : undefined
        }
        aria-label={useStackGroup ? regionLabel : undefined}
        css={toastStackGroupCss}
        role={useStackGroup ? 'group' : undefined}
      >
        {useStackGroup ? (
          <>
            <div
              aria-hidden
              data-cds-toast-stack-hit-bridge
              css={toastStackHitBridgeCss}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onMouseMove={handleMouseMove}
            />
            {stackGroupHintText ? (
              <VisuallyHidden component="span" id={stackGroupHintId}>
                {stackGroupHintText}
              </VisuallyHidden>
            ) : null}
          </>
        ) : null}
        {toastItems}
      </div>
    </div>
  );

  if (renderInPlace) {
    return viewport;
  }

  if (!portalContainer) {
    return null;
  }

  return createPortal(viewport, portalContainer);
}

/* ------------------------------------------------------------------ */
/*  ToastViewportItem                                                  */
/*                                                                     */
/*  All toasts are position: absolute; bottom: 0 (or top: 0).         */
/*  Collapsed: same height as front toast, transform for peek/scale.   */
/*  Expanded: own height, translateY for spacing.                      */
/* ------------------------------------------------------------------ */

function ToastViewportItem({
  item,
  onDismissItem,
  finishDismiss,
  index,
  stackCount,
  expanded,
  externalPaused,
  isBottom,
  isExiting,
  frontHeight,
  naturalHeight,
  offsetY,
  measureRef,
  exitFallbackMs,
  shellApplyGroupRole,
  shellStackedCount,
  shellTabStop,
}: {
  item: ToastQueueItem;
  onDismissItem: (id: string, source?: ToastDismissSource) => void;
  finishDismiss: (id: string) => void;
  index: number;
  stackCount: number;
  expanded: boolean;
  externalPaused: boolean;
  isBottom: boolean;
  isExiting: boolean;
  frontHeight: number;
  naturalHeight: number;
  offsetY: number;
  measureRef: (node: HTMLDivElement | null) => void;
  exitFallbackMs: number;
  shellApplyGroupRole: boolean;
  /** For {@link Toast} shell `role="group"`; `1` when the viewport stack wrapper owns the group. */
  shellStackedCount: number;
  shellTabStop: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  const exitDoneRef = useRef(false);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Last translateY (px) while not exiting — exit slide is capped from this so dismiss does not jump when `expanded` flips with `exitingIds`. */
  const lastLayoutTranslateYRef = useRef(0);

  const fireExitComplete = useCallback(() => {
    if (exitDoneRef.current) {
      return;
    }
    exitDoneRef.current = true;
    if (exitTimerRef.current !== null) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    finishDismiss(item.id);
  }, [finishDismiss, item.id]);

  const isFront = index === 0;
  const clampedIndex = Math.min(index, MAX_VISIBLE_BEHIND);
  const scale = 1 - STACK_SCALE_STEP * clampedIndex;
  const shrink = 1 - scale;
  const collapsedH = frontHeight || naturalHeight;
  const collapsedY = isBottom
    ? -(clampedIndex * STACK_PEEK) - shrink * collapsedH
    : clampedIndex * STACK_PEEK + shrink * collapsedH;
  const expandedY = isBottom
    ? -(offsetY + index * STACK_GAP)
    : offsetY + index * STACK_GAP;

  useLayoutEffect(() => {
    if (!isExiting) {
      lastLayoutTranslateYRef.current = expanded ? expandedY : collapsedY;
      exitDoneRef.current = false;
      return;
    }
    exitDoneRef.current = false;
    exitTimerRef.current = setTimeout(() => {
      exitTimerRef.current = null;
      fireExitComplete();
    }, exitFallbackMs);
    return () => {
      if (exitTimerRef.current !== null) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
  }, [
    collapsedY,
    expanded,
    expandedY,
    exitFallbackMs,
    fireExitComplete,
    isExiting,
  ]);

  const handleTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>) => {
      if (!isExiting) {
        return;
      }
      if (
        event.propertyName !== 'transform' &&
        event.propertyName !== 'opacity'
      ) {
        return;
      }
      fireExitComplete();
    },
    [isExiting, fireExitComplete]
  );

  useToastAutoDismiss(item.durationMs, item.id, onDismissItem, externalPaused);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setMounted(true);
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  /** Slide off-screen toward the edge the stack enters from (bottom vs top provider). */
  const entryTranslateY = isBottom ? '100%' : '-100%';

  const exitScale = expanded ? 1 : scale;

  const toastHeightForExit =
    naturalHeight > 0 ? naturalHeight : collapsedH > 0 ? collapsedH : 32;
  const exitMaxDeltaPx =
    toastHeightForExit * EXIT_TRANSLATE_MAX_HEIGHT_MULTIPLIER;
  const exitBaseTranslateY = isExiting
    ? lastLayoutTranslateYRef.current
    : expanded
    ? expandedY
    : collapsedY;
  const exitTranslateY = isBottom
    ? exitBaseTranslateY + exitMaxDeltaPx
    : exitBaseTranslateY - exitMaxDeltaPx;

  const style: CSSProperties = {
    bottom: isBottom ? 0 : undefined,
    top: isBottom ? undefined : 0,
    zIndex: stackCount - index,
    transformOrigin: isBottom ? 'center bottom' : 'center top',
    // Expanded: no fixed block size — stack grows with full toast content after `Delayed` mounts.
    // Collapsed: `minHeight` (not `height`) so the item can grow when content exceeds the first
    // ResizeObserver read; fixed `height: collapsedH` kept the border box short while overflow
    // stayed visible, drawing past `bottom: 0` and looked clipped vs the viewport inset.
    height: undefined,
    minHeight: isExiting || expanded || !collapsedH ? undefined : collapsedH,
    transform: isExiting
      ? `translateY(${exitTranslateY}px) scale(${exitScale})`
      : !mounted
      ? `translateY(${entryTranslateY})`
      : expanded
      ? `translateY(${expandedY}px) scale(1)`
      : `translateY(${collapsedY}px) scale(${scale})`,
    opacity: isExiting
      ? 0
      : !mounted
      ? 0
      : expanded || isFront || index <= MAX_VISIBLE_BEHIND
      ? 1
      : 0,
  };

  return (
    <div
      ref={measureRef}
      css={toastItemCss}
      data-exiting={isExiting || undefined}
      style={style}
      onTransitionEnd={handleTransitionEnd}
    >
      <Toast
        prefixSlot={item.prefixSlot}
        role={item.role}
        shellApplyGroupRole={shellApplyGroupRole}
        shellStackedCount={shellStackedCount}
        shellTabStop={shellTabStop}
        suffixSlot={item.suffixSlot}
        title={item.title}
        onDismiss={(activation) => onDismissItem(item.id, activation)}
      >
        {item.message}
      </Toast>
    </div>
  );
}
